import { useContext, useState, useEffect, useCallback } from 'react';
import { MovementContext } from './MovementProvider';

// Hooks
import useNotify from "../notify";
import useUser from '../user/useUser';

// Service
import ProductsService from '../../services/productsService';
import MovementService from '../../services/movementService';
import ReferralService from '../../services/referralService';

import { utils } from '../../utils/utils';
import { stockControllUtils } from '../../utils/stockControll-utils';

const useMovement = (movementId) => {
    const [movement, reducer] = useContext(MovementContext);
    const { branch } = useUser();
    const [loading, setLoading] = useState(false);
    const { Notify, variants } = useNotify();

    useEffect(() => {
        const getMovement = () => {
            setLoading(true);
            //TODO: agregar que obtenga los productos en el back para que ya vengan armados los detalles con el nombre/sku del producto
            MovementService
                .Get(movementId)
                .then(move => reducer({
                    type: 'REPLACE',
                    movement: {
                        ...move,
                        details: move.details.map(detail => ({
                            ...detail,
                            description: detail.name || 'Producto No Encontrado',
                            differenceToShow: stockControllUtils.formatDiferrence(detail.difference)
                        }))
                    }
                }))
                .finally(() => setLoading(false));
        }

        if (!!movementId && (!movement || movement.id !== Number(movementId)))
            getMovement();
    }, [movement, movementId, reducer])

    useEffect(() => {
        if (movement && branch && movement.branchCode === undefined)
            edit({ ...movement, branchCode: branch.code });
    }, [movement, branch]); // eslint-disable-line react-hooks/exhaustive-deps

    const searchProduct = async (refId) => {
        setLoading(true);
        const product = await ProductsService
            .GetByRefId(refId)
            .catch(() => console.error(`Product ${refId} not found.`))
            .finally(() => setLoading(false));

        if (!product)
            return;

        return {
            productId: product.productId,
            refId: product.refId,
            colorCode: product.colorCode,
            sizeCode: product.sizeCode || 'U',
            name: product.name
        }
    }

    const searchProducts = async (refIds) => {
        setLoading(true);
        const products = await ProductsService
            .GetAllByRefIds(refIds)
            .catch(() => console.error(`Product information could not be accessed`))
            .finally(() => setLoading(false));

        if (!products)
            return;

        return products.map(product => ({
            productId: product.productId,
            refId: product.refId,
            colorCode: product.colorCode,
            sizeCode: product.sizeCode || 'U',
            name: product.name
        }));
    }

    const addProduct = async (refId, adjustmentTypes) => {
        const parsedRefId = stockControllUtils.parseRefId(refId);

        if (parsedRefId.error)
            return Notify(parsedRefId.error, variants.ERROR);

        const details = movement.details.map(d => d);
        const existing = details.find(existing => compareRefId(existing.refId, parsedRefId.sku));

        if (!existing) {
            const product = await searchProduct(parsedRefId.sku);

            if (!product)
                return Notify(`No se encontró el producto ${parsedRefId.sku}`, variants.ERROR);
            add(details, product, parsedRefId.sku, parsedRefId.quantity, adjustmentTypes);
        } else
            update(existing, parsedRefId.quantity);

        edit({ details })
        return true;
    }

    const addProductMassive = async (refIds) => {
        const details = movement.details.map(d => d);
        const parseRefIds = refIds.map(refId => stockControllUtils.parseRefId(refId));

        const newRefIds = [...new Set(parseRefIds
            .filter(item => !item.error)
            .filter(item => !details.some(existing => compareRefId(existing.refId, item.sku)))
        )]

        const existingRefIds = parseRefIds
            .filter(item => !item.error)
            .filter(item => details.some(detail => compareRefId(detail.refId, item.sku)));

        const notFoundRefIds = [];

        if (newRefIds.length > 0) {
            const products = await searchProducts(newRefIds.map(r => r.sku));

            newRefIds.forEach(parsedRefId => {
                const product = products.find(p => compareRefId(p.refId, parsedRefId.sku));
                if (product)
                    add(details, product, parsedRefId.sku, parsedRefId.quantity);
                else notFoundRefIds.push(parsedRefId.sku);
            });
        }

        existingRefIds
            .forEach(parsedRefId => {
                const detail = details
                    .find(detail => compareRefId(detail.refId, parsedRefId.sku));

                update(detail, parsedRefId.quantity);
            });

        const refIdsWhitError = parseRefIds.filter(item => !!item.error);

        if (refIdsWhitError.length > 0) {
            utils.downloadOnTxt(refIdsWhitError, "skus-con-errores.txt");
            Notify("Algunos productos no se cargaron.", variants.WARNING);
        }

        edit({ details })

        if (notFoundRefIds.length > 0) {
            const link = document.createElement('a');
            link.download = 'NotFound-skus.txt';
            const blob = new Blob([notFoundRefIds.join('\n')], { type: 'text/plain' });
            link.href = window.URL.createObjectURL(blob);
            link.click();
        }
        return true;
    }

    const compareRefId = (existingRefId, refId) =>
        existingRefId.toLowerCase() === refId.toLowerCase();

    const removeProduct = (refId) => {
        if (movement.details.some(d => d.refId === refId)) {
            edit({ details: movement.details.filter(d => d.refId !== refId) })
            return true;
        }
    }

    const editProduct = (refId, payload) => {
        if (movement.details.some(d => d.refId === refId)) {
            const details = movement.details.map(detail => {
                if (detail.refId !== refId) return detail;

                const newDetail = {
                    ...detail,
                    ...payload
                }

                return newDetail;
            });

            edit({ details });
            return true;
        }
    }

    const editAjustmentTypeMassive = async (adjustmentTypes) => {
        movement.details.forEach(function (detail, indice) {
            detail.adjustmentTypes = adjustmentTypes;
        });
        edit({ details: movement.details });
    }

    const edit = useCallback((payload) =>
        reducer({ type: 'EDIT', payload })
        , [reducer]);

    const save = async () => {
        setLoading(true);
        if (movement.id)
            return MovementService
                .Update(movement)
                .then(movement => reducer({ type: 'REPLACE', movement }))
                .finally(() => setLoading(false));

        return MovementService
            .CreateAdjustments(movement)
            .finally(() => setLoading(false));
    }

    const SaveTransfer = async () => {
        const Save = movement.id
            ? MovementService.Update
            : MovementService.CreateTransfer;

        setLoading(true);
        return Save(movement)
            .then(movement => reducer({ type: 'REPLACE', movement }))
            .finally(() => setLoading(false));
    }

    const add = (details, product, refId, quantity, adjustmentTypes = {}) => {
        const detail = {
            productId: product?.productId,
            name: product?.name || 'Producto No Encontrado',
            refId: product ? product.refId : refId,
            colorCode: product?.colorCode,
            sizeCode: product?.sizeCode,
            quantity,
            adjustmentTypes,
            error: product === undefined,
        };

        details.push(detail);
    }

    const update = (detail, quantity) => {
        detail.quantity += quantity
    }

    const clear = useCallback(() =>
        reducer({ type: 'CLEAR' })
        , [reducer]);

    const printReferral = (movementId) =>
        ReferralService.GetVoucher(movementId);

    return {
        movement,
        loading,
        edit,
        save,
        clear,
        addProduct,
        removeProduct,
        editProduct,
        addProductMassive,
        editAjustmentTypeMassive,
        printReferral,
        SaveTransfer,
    };
}

export default useMovement;